import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FiMapPin, FiCalendar, FiArrowRight } from 'react-icons/fi';
import { IoFootball } from 'react-icons/io5';
import Navbar from '../components/Navbar';
import MatchCardSkeleton from '../components/MatchCardSkeleton';
import { matchAPI } from '../services/api';
import logger from '../utils/logger';
import { bangladeshCities } from '../utils/bangladeshLocations';

const Discover = () => {
  const navigate = useNavigate();
  const locationHook = useLocation();
  const searchParams = new URLSearchParams(locationHook.search);
  const searchQuery = searchParams.get('search') || '';

  const authToken = localStorage.getItem('authToken');

  const [filters, setFilters] = useState({
    sport: '',
    location: '',
    skillLevel: '',
  });

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMatches();
  }, [filters, locationHook.search]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const response = await matchAPI.getAllMatches({
        sport: filters.sport || undefined,
        skillLevel: filters.skillLevel || undefined,
        location: filters.location || undefined,
      });
      if (response.success) {
        setMatches(response.matches);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Re-read search from URL on each render so it stays in sync with navigation
  const currentSearchQuery = new URLSearchParams(locationHook.search).get('search') || '';

  const filteredMatches = matches.filter(match => {
    if (!currentSearchQuery) return true;
    const lowerQuery = currentSearchQuery.toLowerCase();
    return (
      (match.title && match.title.toLowerCase().includes(lowerQuery)) ||
      (match.location && match.location.toLowerCase().includes(lowerQuery)) ||
      (match.sport && match.sport.toLowerCase().includes(lowerQuery)) ||
      (match.createdBy?.fullName && match.createdBy.fullName.toLowerCase().includes(lowerQuery))
    );
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  return (
    <div className="bg-dark min-h-screen">
      <Navbar />

      {/* Page Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="space-y-4 mb-10">
            <p className="text-red-500 text-sm font-semibold uppercase tracking-wider">Explore</p>
            <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight">Discover Matches</h1>
            <p className="text-gray-400 text-lg max-w-2xl">Find the perfect match near you. Filter by sport, location, and skill level to get started.</p>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Sport Filter */}
            <div className="relative">
              <select
                name="sport"
                value={filters.sport}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-darkGray border border-gray-700 text-gray-300 rounded-lg focus:outline-none focus:border-red-500 transition text-sm font-semibold appearance-none cursor-pointer hover:border-gray-600"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: '36px',
                }}
              >
                <option value="">Sport</option>
                <option value="football">Football</option>
                <option value="basketball">Basketball</option>
                <option value="tennis">Tennis</option>
              </select>
            </div>

            {/* Location Filter */}
            <div className="relative">
              <select
                name="location"
                value={filters.location}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-darkGray border border-gray-700 text-gray-300 rounded-lg focus:outline-none focus:border-red-500 transition text-sm font-semibold appearance-none cursor-pointer hover:border-gray-600"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: '36px',
                }}
              >
                <option value="">Location</option>
                {bangladeshCities.map((city) => (
                  <option key={city.id} value={city.name}>
                    {city.name} - {city.district}
                  </option>
                ))}
              </select>
            </div>

            {/* Skill Filter */}
            <div className="relative">
              <select
                name="skillLevel"
                value={filters.skillLevel}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-darkGray border border-gray-700 text-gray-300 rounded-lg focus:outline-none focus:border-red-500 transition text-sm font-semibold appearance-none cursor-pointer hover:border-gray-600"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: '36px',
                }}
              >
                <option value="">Skill Level</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
        </div>

        {/* Matches Grid */}
        <div className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              // Show skeleton loaders while loading
              <>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <MatchCardSkeleton key={i} />
                ))}
              </>
            ) : error ? (
              <div className="col-span-full">
                <div className="card-base p-8 text-center">
                  <p className="text-red-500 text-xl mb-4">Error loading matches</p>
                  <p className="text-gray-400 mb-6">{error}</p>
                  <button
                    onClick={loadMatches}
                    className="btn-primary inline-block"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : filteredMatches.length === 0 ? (
              <div className="col-span-full">
                <div className="card-base p-12 text-center">
                  <p className="text-gray-400 text-xl mb-4">No matches found</p>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">Try adjusting your filters or create a new match to get started</p>
                  <button
                    onClick={() => navigate('/create-match')}
                    className="btn-primary inline-block"
                  >
                    Create Match
                  </button>
                </div>
              </div>
            ) : (
              filteredMatches.map((match) => (
                <Link key={match._id} to={`/match/${match._id}`} className="card-base card-hover overflow-hidden group">
                  {/* Match Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={match.matchImage || "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=400&h=300&fit=crop"}
                      alt={match.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    {/* Spots Left Badge */}
                    <div className="absolute top-4 right-4 badge-primary bg-red-600/80 border-red-500">
                      {match.spotsLeft || (match.maxPlayers - (match.participants?.length || 0))} spots
                    </div>
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-dark/60 to-transparent"></div>
                  </div>

                  {/* Match Info */}
                  <div className="p-6 space-y-4">
                    {/* Title */}
                    <h3 className="text-white font-bold text-lg group-hover:text-red-500 transition-colors line-clamp-2">
                      {match.title}
                    </h3>

                    {/* Details */}
                    <div className="space-y-2 text-sm text-gray-400">
                      <div className="flex items-center gap-2">
                        <FiMapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span>{match.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FiCalendar className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span>{match.date && new Date(match.date).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Join Button */}
                    <button className="w-full mt-4 bg-gradient-to-r from-red-600 to-red-500 hover:shadow-glow-red text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 group-hover:scale-105 flex items-center justify-center gap-2">
                      Join Now <FiArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Discover;
